import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'mindgarden-workspace-v1';
const CHANGE_EVENT = 'mindgarden-workspace-change';
const EMPTY_STATE = Object.freeze({
  version: 1,
  tasks: [],
  notes: [],
  imports: {},
  storageError: '',
});

let snapshot;

function parse(value) {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

function uniqueById(items) {
  const seen = new Set();

  return items.filter((item) => {
    if (!item?.id || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function normalizeTask(source, item, index, overrides = {}) {
  return {
    id: `${source}:${item.id ?? index}`,
    title: String(item.title || '').trim(),
    status: overrides.status || item.status || 'todo',
    project: overrides.project ?? item.project ?? 'General',
    scheduledDate: overrides.scheduledDate ?? item.scheduledDate ?? '',
    scheduledTime: overrides.scheduledTime ?? item.scheduledTime ?? '',
    estimatedMinutes: Number(
      overrides.estimatedMinutes ?? item.estimatedMinutes
    ) || 25,
    createdAt: item.createdAt || new Date().toISOString(),
  };
}

function normalizeNote(source, item, index, type) {
  return {
    id: `${source}:${item.id ?? index}`,
    type,
    text: String(item.text || '').trim(),
    createdAt:
      item.createdAt
      || item.t
      || item.timestamp
      || new Date().toISOString(),
    linkedTaskId: item.linkedTaskId || null,
  };
}

function getDuration(startTime, endTime) {
  if (!startTime || !endTime) return 25;

  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  const minutes = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);

  return Math.max(5, minutes);
}

function readSource(key, mapper) {
  const rawValue = localStorage.getItem(key);

  if (!rawValue) {
    return {
      found: false,
      reliable: false,
      items: [],
    };
  }

  const parsedValue = parse(rawValue);

  if (!parsedValue) {
    return {
      found: true,
      reliable: false,
      items: [],
    };
  }

  try {
    return {
      found: true,
      reliable: true,
      items: mapper(parsedValue).filter((item) => item.title || item.text),
    };
  } catch {
    return {
      found: true,
      reliable: false,
      items: [],
    };
  }
}

function initializeWorkspace() {
  if (typeof window === 'undefined') return EMPTY_STATE;

  const current = parse(localStorage.getItem(STORAGE_KEY));
  const drafts = [
    'mindgarden.workspace.v1',
    'mindgarden_workspace_v1',
  ]
    .map((key) => parse(localStorage.getItem(key)))
    .filter(Boolean);

  let nextState = {
    version: 1,
    tasks: uniqueById([
      ...(current?.tasks || []),
      ...drafts.flatMap((draft) => draft.tasks || []),
    ]),
    notes: uniqueById([
      ...(current?.notes || []),
      ...drafts.flatMap((draft) => draft.notes || []),
    ]),
    imports: {
      ...(current?.imports || {}),
    },
    storageError: '',
  };

  const sources = {
    planner: readSource('mindgarden-planner-v1', (data) => (
      Array.isArray(data.blocks)
        ? data.blocks.map((item, index) => normalizeTask(
          'planner',
          item,
          index,
          {
            scheduledDate: data.date || '',
            scheduledTime: item.startTime || '',
            estimatedMinutes: getDuration(item.startTime, item.endTime),
          }
        ))
        : []
    )),
    grove: readSource('mindgarden_grove_v1', (data) => (
      Array.isArray(data)
        ? data.map((item, index) => normalizeTask(
          'grove',
          item,
          index,
          {
            status: item.column === 'harvested' ? 'completed' : 'todo',
            project:
              item.mentorKey === 'leo'
                ? 'General'
                : item.tags?.[0] || 'General',
            scheduledDate: item.dueDate || '',
          }
        ))
        : []
    )),
    thoughts: readSource('thoughtGarden.v1', (data) => (
      Array.isArray(data)
        ? data.map((item, index) => normalizeNote(
          'thought',
          item,
          index,
          'reflection'
        ))
        : []
    )),
    greenhouse: readSource('mindgarden-greenhouse-v1', (data) => (
      Array.isArray(data)
        ? data.map((item, index) => normalizeNote(
          'greenhouse',
          item,
          index,
          'idea'
        ))
        : []
    )),
    weeds: readSource('weedList.v2', (data) => (
      Array.isArray(data.weeds)
        ? data.weeds.map((item, index) => normalizeNote(
          'weed',
          item,
          index,
          'reflection'
        ))
        : []
    )),
  };

  Object.entries(sources).forEach(([name, result]) => {
    if (
      nextState.imports[name]
      || !result.found
      || !result.reliable
    ) {
      return;
    }

    const isTaskSource = name === 'planner' || name === 'grove';

    nextState = {
      ...nextState,
      tasks: isTaskSource
        ? uniqueById([...nextState.tasks, ...result.items])
        : nextState.tasks,
      notes: isTaskSource
        ? nextState.notes
        : uniqueById([...nextState.notes, ...result.items]),
      imports: {
        ...nextState.imports,
        [name]: 1,
      },
    };
  });

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextState));
  } catch {
    nextState = {
      ...nextState,
      storageError: 'Changes could not be saved on this device.',
    };
  }

  return nextState;
}

function getSnapshot() {
  if (!snapshot) snapshot = initializeWorkspace();
  return snapshot;
}

function subscribe(listener) {
  function synchronize(event) {
    if (event.type !== CHANGE_EVENT && event.key !== STORAGE_KEY) return;

    snapshot =
      parse(localStorage.getItem(STORAGE_KEY))
      || snapshot
      || EMPTY_STATE;

    listener();
  }

  window.addEventListener(CHANGE_EVENT, synchronize);
  window.addEventListener('storage', synchronize);

  return () => {
    window.removeEventListener(CHANGE_EVENT, synchronize);
    window.removeEventListener('storage', synchronize);
  };
}

function commit(updater) {
  const nextState = {
    ...updater(getSnapshot()),
    version: 1,
    storageError: '',
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextState));
    snapshot = nextState;
  } catch {
    snapshot = {
      ...nextState,
      storageError: 'Changes could not be saved on this device.',
    };
  }

  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function createId(type) {
  return `${type}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
}

export default function useWorkspace() {
  const state = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => EMPTY_STATE
  );

  const addTask = useCallback((values) => {
    const newTask = normalizeTask(
      'workspace',
      {
        ...values,
        id: createId('task'),
        createdAt: new Date().toISOString(),
      },
      0
    );

    commit((current) => ({
      ...current,
      tasks: [...current.tasks, newTask],
    }));

    return newTask;
  }, []);

  const updateTask = useCallback((taskId, updates) => {
    commit((current) => ({
      ...current,
      tasks: current.tasks.map((item) => (
        item.id === taskId
          ? {
            ...item,
            ...updates,
            id: taskId,
          }
          : item
      )),
    }));
  }, []);

  const addNote = useCallback((values) => {
    const newNote = normalizeNote(
      'workspace',
      {
        ...values,
        id: createId('note'),
        createdAt: new Date().toISOString(),
      },
      0,
      values.type || 'note'
    );

    commit((current) => ({
      ...current,
      notes: [...current.notes, newNote],
    }));

    return newNote;
  }, []);

  const updateNote = useCallback((noteId, updates) => {
    commit((current) => ({
      ...current,
      notes: current.notes.map((item) => (
        item.id === noteId
          ? {
            ...item,
            ...updates,
            id: noteId,
          }
          : item
      )),
    }));
  }, []);

  return {
    ...state,
    addTask,
    updateTask,
    addNote,
    updateNote,
  };
}